import { resolve, relative } from "node:path";
import { existsSync, readFileSync } from "node:fs";
import { globSync } from "glob";
import type {
  CaiConfig,
  DriftReport,
  Claim,
  DriftCheckerName,
  DriftContext,
  StalenessThresholds,
} from "../types.js";
import { extractClaims } from "./claims.js";
import { computeScore } from "./scoring.js";
import { createDriftRegistry, runCheckerRegistry, selectDriftRegistry } from "./registry.js";
import { scanProjectModel } from "../scanner/manifest.js";
import { getChangedFiles } from "../git.js";
import { findDeletionCommit } from "../git/blame.js";
import { weightedScore } from "./severity.js";
import { aggregateByFile, readQueries } from "../telemetry/query-log.js";
import { appendHistory } from "./history.js";

export const DEFAULT_STALENESS: StalenessThresholds = {
  warnDays: 30,
  errorDays: 90,
  warnCommits: 50,
  errorCommits: 200,
};

/** Run full drift detection across all scaffold files */
export async function runDriftCheck(
  config: CaiConfig,
  opts: {
    verbose?: boolean;
    staleDays?: number;
    staleCommits?: number;
    only?: DriftCheckerName[];
    skip?: DriftCheckerName[];
    incremental?: boolean;
  } = {}
): Promise<DriftReport> {
  const { projectRoot, scaffoldRoot } = config;
  const staleness = resolveStalenessThresholds(
    config.settings.staleness,
    opts
  );

  // Find all markdown files in scaffold (exclude auto-generated codex files from drift checking)
  let scaffoldFiles = findScaffoldFiles(projectRoot, scaffoldRoot)
    .filter((f) => !f.includes("/codex/"));

  // --incremental: restrict to files that have uncommitted changes
  if (opts.incremental) {
    const changed = await getChangedFiles(projectRoot);
    const changedSet = new Set(changed.map((f) => resolve(projectRoot, f)));
    const filtered = scaffoldFiles.filter((f) => changedSet.has(f));
    // Fall back to all files if nothing matches (e.g. clean working tree)
    if (filtered.length > 0) scaffoldFiles = filtered;
  }

  const allClaims: Claim[] = [];
  const project = scanProjectModel(projectRoot);

  // Extract claims from all files
  for (const filePath of scaffoldFiles) {
    const source = relative(projectRoot, filePath);
    const claims = extractClaims(filePath, source);
    allClaims.push(...claims);
  }

  const context: DriftContext = {
    projectRoot,
    subProjectRoot: config.subProjectRoot,
    scaffoldRoot,
    scaffoldFiles,
    claims: allClaims,
    project,
    config,
    staleness,
  };

  const registry = selectDriftRegistry(createDriftRegistry(), {
    only: opts.only,
    skip: opts.skip,
  });

  const { issues: allIssues, summaries: checkerSummaries } = await runCheckerRegistry(context, registry);

  // Enrich MISSING_PATH issues with git context — find the commit that deleted/renamed the file
  for (const issue of allIssues) {
    if (issue.code === "MISSING_PATH" && issue.claim) {
      const info = findDeletionCommit(projectRoot, issue.claim.value);
      if (info) {
        issue.gitContext = {
          commit: info.commit,
          message: info.message,
          ago: info.ago,
          renamedTo: info.renamedTo,
        };
      }
    }
  }

  const score = computeScore(allIssues);

  // Hot-path-aware weighted score from telemetry (falls back to type-weighted only)
  const queries = readQueries(projectRoot, { sinceMs: Date.now() - 7 * 24 * 60 * 60 * 1000 });
  const aggregations = aggregateByFile(queries);
  const weighted = weightedScore(allIssues, aggregations);

  // Append a history snapshot so `cai check --history` can show trends
  const errors = allIssues.filter((i) => i.severity === "error").length;
  const warnings = allIssues.filter((i) => i.severity === "warning").length;
  appendHistory(projectRoot, {
    score,
    weightedScore: weighted.usedTelemetry ? weighted.score : undefined,
    errors,
    warnings,
    filesChecked: scaffoldFiles.length,
  });

  return {
    score,
    weightedScore: weighted.score,
    usedTelemetry: weighted.usedTelemetry,
    issues: allIssues,
    filesChecked: scaffoldFiles.length,
    timestamp: new Date().toISOString(),
    projectRoot,
    subProjectRoot: config.subProjectRoot,
    diagnostics: opts.verbose
        ? {
            scaffoldFiles: scaffoldFiles.map((file) => relative(projectRoot, file)),
          claimsByKind: {
            path: allClaims.filter((claim) => claim.kind === "path").length,
            command: allClaims.filter((claim) => claim.kind === "command").length,
            dependency: allClaims.filter((claim) => claim.kind === "dependency").length,
            version: allClaims.filter((claim) => claim.kind === "version").length,
          },
          project: {
            rootManifestType: project.rootManifest?.type ?? null,
            rootManifestName: project.rootManifest?.name ?? null,
            workspaceCount: project.workspaces.length,
            workspaceNames: project.workspaces
              .map((workspace) => workspace.manifest.name ?? workspace.path)
              .slice(0, 5),
            workspaceDependencyCount: project.workspaceDependencies.length,
            commandCount: project.commands.length,
            sampleCommands: project.commands.slice(0, 5),
          },
          checkerSummaries,
        }
      : undefined,
  };
}

/** Find all markdown files that are part of the scaffold */
export function findScaffoldFiles(
  projectRoot: string,
  scaffoldRoot: string
): string[] {
  const scaffoldPatterns = [
    "context/*.md",
    "patterns/*.md",
    "codex/*.md",
    "ROUTER.md",
    "AGENTS.md",
    "SETUP.md",
    "SYNC.md",
  ];

  // Load .caiignore patterns (from scaffold root and project root)
  const ignorePatterns = loadCaiIgnore(scaffoldRoot, projectRoot);

  const files: string[] = [];

  // Search inside scaffold root (handles .cai/, legacy hidden dirs, and root layouts)
  for (const pattern of scaffoldPatterns) {
    const matches = globSync(pattern, {
      cwd: scaffoldRoot,
      absolute: true,
      ignore: ["node_modules/**", ...ignorePatterns],
    });
    files.push(...matches);
  }

  // Also check project root for tool config files (CLAUDE.md, etc.)
  if (scaffoldRoot !== projectRoot) {
    for (const name of ["CLAUDE.md", ".cursorrules", ".windsurfrules"]) {
      const matches = globSync(name, {
        cwd: projectRoot,
        absolute: true,
        ignore: ["node_modules/**", ...ignorePatterns],
      });
      files.push(...matches);
    }
  }

  // Deduplicate
  return [...new Set(files)];
}

/**
 * Load .caiignore patterns from scaffold root and project root.
 * Each non-empty, non-comment line is treated as a glob pattern.
 * Patterns are relative to the directory they're declared in.
 */
function loadCaiIgnore(scaffoldRoot: string, projectRoot: string): string[] {
  const patterns: string[] = [];
  const locations = [...new Set([scaffoldRoot, projectRoot])];

  for (const dir of locations) {
    const ignoreFile = resolve(dir, ".caiignore");
    if (!existsSync(ignoreFile)) continue;
    try {
      const lines = readFileSync(ignoreFile, "utf-8")
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l && !l.startsWith("#"));
      patterns.push(...lines);
    } catch {
      // Non-fatal — ignore unreadable files
    }
  }

  return patterns;
}

function resolveStalenessThresholds(
  settings: Partial<StalenessThresholds> | undefined,
  opts: { staleDays?: number; staleCommits?: number }
): StalenessThresholds {
  return {
    warnDays: opts.staleDays ?? settings?.warnDays ?? DEFAULT_STALENESS.warnDays,
    errorDays: settings?.errorDays ?? DEFAULT_STALENESS.errorDays,
    warnCommits: opts.staleCommits ?? settings?.warnCommits ?? DEFAULT_STALENESS.warnCommits,
    errorCommits: settings?.errorCommits ?? DEFAULT_STALENESS.errorCommits,
  };
}
